/* Серверный режим экрана «Создать кавер» на движке шаблона (план вехи 3, шаги 2–4 экрана; lane plan text-cover §2.1).
   createCover(createCoverServer) (features/cover/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SRV спрашивает этот объект:
   списки стилей и длительностей, цена, готовность, голос, песня, создание, хвост и итог — только из формы и сервиса заказа кадра
   (OrderService привязки EngineFrame). Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние формы (S), свой мост загрузки, своё зеркало входов и свой хвост BotRun, поэтому песня, согласие и заказ одного
     приложения или владельца не видны и не уходят под другим;
   - S — вид входов сервиса: входы изменились не формой (повтор из «Истории», заявка родной формы, загрузка) — readCover → S;
     правка формы — writeCover → входы (одна точка записи — syncFoot шаблона), только опубликованными ключами (cover-inputs.ts);
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис;
   - цена на кнопке — цена длительности формы (constraints.cover.durations[].price) или скидки; в заказ уходит цена сервиса; если они
     расходятся у платной работы, создание закрыто: «Цена этого выбора пока не подтверждена» (родная форма, D11);
   - песня — мост загрузки (media-bridge.ts): поле шаблона или JSON-ключ контракта song_upload_id, лист «Права на песню», одна очередь,
     «Продолжить загрузку» тем же id; песня повтора — превью сервера (не вышло — загрузить заново, решение Q7); «Мои треки» —
     готовые записи «Истории» (source_order_id), слушать их здесь нельзя (Q-C3);
   - пример стиля звучит только с preview_url (Q-C1: без него значка примера нет). */
import { endpoints } from '../../api';
import type { HistoryOrder } from '../../api/history';
import type { Voice } from '../../api/types';
import type { UserVoice } from '../../api/user-voices';
import { voiceOrb } from '../../bridge/voice-orb';
import { uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, markSeen, rememberCircleBalance, toolPrice } from '../studio/circle/tool-helpers';
import { tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { studioCheapOfferAt, studioExpectedPrice, type StudioApp, type StudioForm } from '../studio/studio-api';
import { record } from '../studio/studio-content';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentOptions, type ConsentSheet, type ConsentTexts } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { isImproveKind } from '../studio/engine-kit/improve-service';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaFile, mediaPolicy, type MediaBridge, type MediaItem, type MediaPolicy } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { createResultMedia, type ResultMedia } from '../studio/engine-kit/result-media';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { createVoicePick, createVoiceSample, readyClone, type VoicePick, type VoiceSample } from '../studio/engine-kit/voice-pick';
import { patchSpec, readText, specKey } from '../reels/form-keys';
import { filePeaks, mmss } from '../voice/voice-model';
import { coverLists, readCover, writeCover, type CoverLists, type CoverView } from './cover-inputs';
import { coverVariants, resample, type CoverVariant } from './cover-result';
import { transferCoverResult } from './cover-transfer';
import { coverMissing, coverSupported, songSlot, songValue, type CoverPolicy } from './cover-policy';

/* ---------- контракт с движком (features/cover/engine.ts, serve({...})) ---------- */

/** Песня в рамке (S.file шаблона): mid — запись моста загрузки (или RESTORE — читается превью), mine — трек «Моих треков» (src — заказ) */
export interface CoverFile {
  name: string;
  size: number;
  url: string;
  dur: number;
  kind: 'audio';
  peaks?: number[];
  rec?: boolean;
  mine?: boolean;
  src?: number;
  mid?: string;
}
/** Состояние формы шаблона (S) */
export interface CoverState {
  sty: number | 'own';
  list: Array<number | 'own'>;
  text: string;
  desc: string;
  open: boolean;
  vm: 'src' | 'voice';
  voice: unknown;
  dur: 'frag' | 'full';
  from: number;
  file: CoverFile | null;
  cheap: boolean;
  sh: { view: string } | null;
}
/** Стиль в виде шаблона (STY, OWN) */
export interface EngineStyle {
  id: number | 'own';
  n: string;
  d: string;
  c: string[];
  th?: string[];
  pop?: number;
  isNew?: boolean;
  b?: string;
  own?: boolean;
}
/** Строка «Моих треков» (MINE шаблона) и заказ, из которого она */
export interface MineRow {
  name: string;
  what: string;
  sty: number | null;
  dur: number;
  size: number;
  src: number;
}
/** Голос в виде шаблона (лист «Ещё настройки», снимок заказа): шар — строка CSS, is_s2s — строго с сервера */
export interface EngineVoice {
  id: number;
  name: string;
  orb: string;
  mine: boolean;
  is_s2s: boolean;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  voices(): unknown[];
  myVoices(): unknown[];
  [key: string]: unknown;
}
interface EngineUpload {
  o: { mb?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
interface EngineSettings {
  sync(o?: { force?: boolean }): void;
  set(k: string, v: unknown): void;
  close(): void;
}
interface Limits {
  frag: number;
  max: number;
  min: number;
  tmin: number;
  tmax: number;
}
/** Крючки, которые движок отдаёт serve (engine.ts, вставка после OWN_URL): tail, up, foot, set и rail — геттеры */
export interface CoverEngineHooks {
  S: CoverState;
  STY: EngineStyle[];
  OWN: EngineStyle;
  CHIPS: { add: string[][]; own: string[][] };
  DURS: string[][];
  THEMES: string[];
  MINE: MineRow[];
  SAM: { id: unknown; where: unknown; t: number };
  PLAY: { on: boolean; pos: number };
  RP: { i: number; on: boolean; pos: number; t0: number; raf: number };
  REC: { st: 'idle' | 'asking' | 'rec'; sec: number; sim: boolean; inst: unknown; n: number };
  readonly tail: BotRunApi;
  readonly up: EngineUpload;
  readonly foot: EngineFoot;
  readonly set: EngineSettings;
  readonly rail: { sync(o?: unknown): void; paint(): void };
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  artHTML(style: EngineStyle | null, o?: Record<string, unknown>): string;
  setLimits(o: Partial<Limits>): void;
  limits(): Limits;
  paintPage(): void;
  setFile(f: CoverFile | null): void;
  songPaint(): void;
  paintUnder(): void;
  syncText(): void;
  syncFoot(): void;
  railShow(): void;
  rpPaint(full?: boolean): void;
  fpaint(full?: boolean): void;
  fstop(rewind: boolean): void;
  samStop(quiet?: boolean): void;
  samPaint(): void;
  liveStop(): void;
  sheetClose(): void;
  buildList(): void;
  recCancel(quiet?: boolean): void;
  paintMine(): void;
  fromOf(f: CoverFile): number;
  isOwn(): boolean;
}
type Missing = 'load' | 'blocked' | 'off' | 'unavailable' | '';
/** Снимок входа заказа (r.snap BotRun, runSnap шаблона): из тела заявки, а без неё — из формы */
export interface CoverSnap {
  [key: string]: unknown;
  file: CoverFile;
  sty: EngineStyle;
  text: string;
  voice: number | null;
  vname: string;
  dur: 'frag' | 'full';
  from: number;
  to: number;
  len: number;
  cheap: boolean;
  peaks: number[][];
}

export interface CoverServer {
  ui<U extends object>(base: U): ServerUi<U>;
  open(o: object): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T, extra?: unknown): T;
  art(id: unknown, o?: Record<string, unknown>): string;
  has(k: 'own' | 'mine' | 'voice' | 'dur' | 'wish'): boolean;
  voice(id: unknown): EngineVoice | null;
  clone(): EngineVoice | null;
  pickVoice(id: unknown): void;
  price(d: string, cheap: boolean): number;
  unit(): string;
  note(): string;
  missing(): Missing | null;
  ready(): string;
  missText(kind: string): string;
  label(): string | null;
  improveKind(): string;
  upOpts(): { accept: string; tx(template: string, ...values: unknown[]): string; onPick(files: File[]): void };
  remove(): void;
  write(): void;
  canSample(id: unknown): boolean;
  sample(id: number, onEnd: () => void): boolean;
  sampleStop(): void;
  loadMine(): void;
  mineNote(): string;
  chooseMine(i: number): void;
  canRecord(): boolean;
  micText(kind: 'off' | 'denied'): string;
  recorded(x: { sec: number; blob: Blob | null; url: string | null }): void;
  fplay(f: CoverFile): boolean;
  start(btn: HTMLElement): void;
  steps(): string[][];
  snap(): CoverSnap;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  /** Подпись итога (T.doneP): доставка заказа — бот уже прислал, пришлёт или не присылает (tail-done) */
  doneText(r: BotRunRun): string;
  download(i: number): Promise<{ data: Blob; name: string }>;
  picked(i: number): void;
  play(i: number, at?: number): boolean;
  audioStop(rewind: boolean): void;
  leave(go: () => void, replace: boolean): void;
  click(d: DOMStringMap, el: HTMLElement, r: unknown): boolean;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest: string): string };
  wave: { N: number };
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
/** accept рамки BotCore (её проверка типа — до моста): любой звук и расширения, у которых браузер не называет тип (.m4a); точные типы и
    предел — у моста по политике поля формы */
const ACCEPT = 'audio/*,.m4a,.mp3,.wav,.ogg,.oga,.opus,.webm,.aac,.flac';
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Запись моста, превью которой ещё читается (заглушка в рамке) */
const RESTORE = 'restore';
/** Имена типов звука для строки ограничений рамки («MP3, WAV или M4A, до 10 минут» шаблона) */
const NAMES: Record<string, string> = { 'audio/mpeg': 'MP3', 'audio/wav': 'WAV', 'audio/mp4': 'M4A', 'audio/ogg': 'OGG', 'audio/webm': 'WebM' };
/** Лист согласия кавера (KC): права на песню — строкой родной формы */
const RIGHTS: ConsentTexts = {
  title: 'Права на песню',
  row: 'Только свои песни или песни с правами',
  sub: 'Подтверждаю права на эту запись',
  miss: 'Подтвердите права на песню',
};
const num = (value: unknown) => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isSafeInteger(n) && n > 0 ? n : undefined;
};
const flat = () => Array<number>(core().wave?.N ?? 52).fill(0.07);
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
function engineVoice(voice: Voice): EngineVoice {
  return { id: voice.id, name: voice.name, orb: voiceOrb(voice) ?? '', mine: !!voice.is_mine, is_s2s: voice.is_s2s === true };
}
/** Голос каталога оболочки (ui.voices / ui.myVoices) в виде шаблона; is_s2s — только явное true */
function shellVoice(raw: unknown): EngineVoice | null {
  const v = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const id = num(v?.id);
  if (!v || id === undefined) return null;
  return { id, name: typeof v.name === 'string' ? v.name : '', orb: typeof v.orb === 'string' ? v.orb : '', mine: v.mine === true || v.is_mine === true, is_s2s: v.is_s2s === true };
}
/** «Мой голос · клон»: голос каталога клона (voice_id), шар — из его цветов */
function cloneVoice(clone: UserVoice | null): EngineVoice | null {
  if (!clone?.voice_id) return null;
  return { id: clone.voice_id, name: clone.name, orb: voiceOrb({ orb: clone.orb, avatar_url: null } as Pick<Voice, 'orb' | 'avatar_url'>) ?? '', mine: true, is_s2s: true };
}
/** Строка «Моих треков» из готового заказа «Истории» (вкладка «Аудио») */
function mineRow(item: HistoryOrder): MineRow {
  const ms = item.result?.duration_ms;
  return {
    name: item.title || uiFormat('Трек №{0}', item.id),
    what: uiText(item.kind === 'cover' ? 'Кавер' : 'Своя запись'),
    sty: null,
    dur: typeof ms === 'number' && ms > 0 ? Math.round(ms / 1000) : 0,
    size: 0,
    src: item.id,
  };
}
/** Причина родной формы (coverMissing) словами профиля: шаблоны с числом — по своему шаблону */
function reasonText(reason: string, policy: CoverPolicy): string {
  if (/^Песня длиннее /.test(reason)) return uiFormat('Песня длиннее {0} минут', Math.round(policy.maxMs / 60000));
  if (/^Опишите стиль, от /.test(reason)) return uiFormat('Опишите стиль, от {0} знаков', policy.styleMin);
  if (/^Описание длиннее /.test(reason)) return uiFormat('Описание длиннее {0} знаков', policy.styleMax);
  return uiText(reason);
}

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  mirror: InputsMirror;
  driver: RunDriver;
  /** Итог: файлы вариантов по нажатию (K5), плеер и «Скачать» */
  media: ResultMedia;
  /** Заказ и ревизия, волны которых уже на карточке */
  doneKey: string;
  doneRun: BotRunRun | null;
  moving: boolean;
  /** Форма, для которой собраны списки (lists) */
  form: StudioForm | null;
  lists: CoverLists | null;
  /** Раскладка формы на экране: опубликованные части и стили */
  layout: string;
  /** Превью какой загрузки читается сейчас */
  restoring: string | null;
  said: string;
  consent: ConsentSheet | null;
  pick: VoicePick;
  sample: VoiceSample;
  clone: EngineVoice | null;
  cloneAsked: boolean;
  /** «Мои треки»: номер запроса и что показать в листе */
  mineSeq: number;
  mineState: 'load' | 'ok' | 'empty' | 'error';
  peek: Intent | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createCoverServer(h: CoverEngineHooks): CoverServer {
  const { S } = h;
  /* обложки «Истории» (BotCover.art) — по стилям шаблона, до того как форма заменит списки */
  const STY0 = h.STY.map(s => ({ ...s, c: s.c.slice() }));
  const OWN0 = { ...h.OWN, c: h.OWN.c.slice() };
  /* демо-треков шаблона в серверном режиме нет: строки — только из «Истории» */
  h.MINE.splice(0);
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относится S (форма шаблона) — объект, а не ключ: ключи ServerStudio повторяются после нового входа в раздел */
  let painted: Frame | null = null;
  /** Кадр, из формы которого сейчас списки шаблона (STY, DURS…): другой кадр собирает их заново */
  let filled: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;

  const cur = () => (bound && bound === frame ? frame : null);
  /** Цена длительности без скидки: расчёт заказа для текущей, иначе цена длительности из списка или формы под неё */
  const basePrice = (d: string) => {
    const f = cur() ?? frame;
    const s = f?.svc.get();
    const form = s?.form;
    if (!f || !s || !form) return toolPrice(f?.app.price).amount ?? 0;
    const current = d === S.dur;
    if (form.chargeable === false) return current ? f.svc.derive().price : studioExpectedPrice(form, patchSpec(form, s.inputs, { duration: d }));
    const duration = f.lists?.durations.find(item => item.id === d);
    if (duration?.price !== null && duration?.price !== undefined) return duration.price;
    return current ? f.svc.derive().price : studioExpectedPrice(form, patchSpec(form, s.inputs, { duration: d }));
  };
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  const songKey = (f: Frame) => {
    const form = f.svc.get().form;
    return (form && songSlot(form)?.key) || 'song_upload_id';
  };
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const published = (f: Frame | null, key: string) => {
    const form = f?.svc.get().form;
    return !form || !!specKey(form, key);
  };

  /* ---- списки шаблона из формы ---- */
  function fillLists(f: Frame) {
    filled = f;
    /* «Свой стиль» — карточка шаблона: её подписи — словарём профиля */
    h.OWN.n = uiText('Свой стиль');
    h.OWN.d = uiText('Опишите жанр, настроение и инструменты');
    const L = f.lists;
    if (!L) return;
    const { policy } = L;
    h.STY.splice(
      0,
      h.STY.length,
      ...L.styles.map(s => ({ id: s.id, n: s.n, d: s.d, c: s.c.slice(), th: s.th.slice(), pop: s.pop, isNew: s.isNew, b: s.badge === 'hit' ? uiText('Хит') : s.badge === 'new' ? uiText('Новое') : '' })),
    );
    h.THEMES.splice(0, h.THEMES.length, ...policy.themes);
    h.CHIPS.add.splice(0, h.CHIPS.add.length, ...policy.addChips.map(chip => [chip, chip]));
    h.CHIPS.own.splice(0, h.CHIPS.own.length, ...policy.ownChips.map(chip => [chip, chip]));
    h.DURS.splice(
      0,
      h.DURS.length,
      ...L.durations.map(d => [
        d.id,
        uiText(d.title),
        uiText(d.id === 'full' ? 'за песню' : 'за фрагмент'),
        d.id === 'full' ? uiFormat('до {0} минут', Math.round(policy.maxMs / 60000)) : uiText('любая минута песни'),
      ]),
    );
    h.setLimits({ frag: L.fragSec, max: Math.round(policy.maxMs / 1000), min: Math.round(policy.minMs / 1000), tmin: policy.styleMin, tmax: policy.styleMax });
    const form = f.svc.get().form;
    const slot = form ? songSlot(form) : null;
    if (slot) h.up.o.mb = slot.maxBytes / MB;
    /* лента стилей — заново по новым спискам («Свой стиль» — только если он опубликован) */
    S.list = [];
  }

  /* ---- входы ↔ S ---- */
  const viewOf = (): CoverView => ({
    sty: S.sty,
    text: S.sty === 'own' ? S.desc : S.text,
    dur: S.dur,
    from: S.from,
    vm: S.vm,
    voice: S.vm === 'voice' ? (num(S.voice) ?? null) : null,
    source: S.file?.mine ? (S.file.src ?? null) : null,
  });
  const songSec = () => (S.file && S.file.dur > 0 ? S.file.dur : null);
  /** Вид входов → S; true — S изменилось */
  function apply(v: CoverView): boolean {
    const before = JSON.stringify([S.sty, S.text, S.desc, S.dur, S.from, S.vm, S.voice]);
    S.sty = v.sty;
    if (v.sty === 'own') S.desc = v.text;
    else S.text = v.text;
    S.dur = v.dur;
    S.from = v.from;
    S.vm = v.vm;
    S.voice = v.voice;
    if (!S.list.some(id => String(id) === String(S.sty))) S.list = [];
    return before !== JSON.stringify([S.sty, S.text, S.desc, S.dur, S.from, S.vm, S.voice]);
  }
  /** Запись входов формой: сервис заперт заявкой, проверкой или созданием — нет; идёт только загрузка песни — можно (сервис пишет ключ
      песни функцией от текущих входов, правка других ключей не теряется) */
  function commit(f: Frame, next: (prev: Record<string, string>) => Record<string, string>): boolean {
    const { s, d } = live(f);
    if (!d.locked) return f.mirror.commit(next);
    if (s.intent || d.submissionBlocked || !s.uploadingFile) return false;
    let wrote: Record<string, string> | null = null;
    f.svc.setters.inputs(current => {
      const value = next(current);
      if (sameInputs(value, current)) return current;
      wrote = value;
      return value;
    });
    if (wrote) f.mirror.seen(wrote);
    return !!wrote;
  }

  /* ---- песня ---- */
  const placeholder = (): CoverFile => ({ name: uiText('Песня'), size: 0, url: '', dur: 0, kind: 'audio', peaks: flat(), mid: RESTORE });
  function mineFile(source: number): CoverFile {
    const row = h.MINE.find(item => item.src === source);
    return { name: row?.name ?? uiFormat('Трек №{0}', source), size: row?.size ?? 0, url: '', dur: row?.dur ?? 0, kind: 'audio', mine: true, src: source, peaks: flat() };
  }
  /** Политика ключа песни: поле шаблона или JSON-ключ контракта; длина — по политике кавера (6 с … 10:00), строка ограничений — шаблона */
  function songPolicy(f: Frame, key: string): MediaPolicy | null {
    const form = f.svc.get().form;
    const slot = form ? songSlot(form) : null;
    if (!form || !slot || slot.key !== key) return null;
    const policy = f.lists?.policy;
    const p = mediaPolicy(form, key, { minMs: policy?.minMs ?? 6000, maxMs: policy?.maxMs ?? 600000, note: server.note() });
    if (!p) return null;
    return slot.screen && !p.raw ? { ...p, raw: { kind: 'audio', purpose: 'order_input' } } : p;
  }
  /** Волна песни из самого файла (WebAudio): рамка и место под ней — на месте */
  function wave(file: CoverFile, blob: File | null) {
    if (!blob) return;
    void filePeaks(blob, core().wave?.N ?? 52)
      .then(result => {
        if (!result || S.file !== file) return;
        file.peaks = result.peaks;
        if (!(file.dur > 0) && result.durationMs > 0) file.dur = Math.round(result.durationMs / 1000);
        if (onForm()) h.songPaint();
      })
      .catch(() => {});
  }
  function put(f: Frame, item: MediaItem) {
    const ms = item.meta.durationMs;
    const file: CoverFile = {
      name: item.rec ? uiFormat('Запись {0}', ++h.REC.n) : item.restored ? uiText('Песня') : item.name,
      size: item.size,
      url: item.url,
      dur: ms && ms > 0 ? Math.round(ms / 1000) : 0,
      kind: 'audio',
      peaks: flat(),
      mid: item.id,
      ...(item.rec ? { rec: true } : {}),
    };
    if (f.restoring === item.uploadId) f.restoring = null;
    h.recCancel(true);
    h.setFile(file);
    /* setFile шаблона ставит окно фрагмента в начало: у песни повтора начало — из входов */
    const form = f.svc.get().form;
    if (item.restored && form && f.lists) S.from = readCover(form, f.lists, f.svc.get().inputs).from;
    if (onForm()) {
      h.songPaint();
      if (!item.restored) h.ui().buzz();
    }
    wave(file, item.file);
  }
  function drop(item: MediaItem | null) {
    const file = S.file;
    if (!file || file.mine || (item ? file.mid !== item.id : file.mid !== RESTORE)) return;
    h.setFile(null);
    if (onForm()) h.songPaint();
  }
  /** Запись ключа песни мостом (JSON-ключ контракта): кодирование — по ключу формы, как родная форма (patchSpec) */
  function writeSong(f: Frame, key: string, value: string | null) {
    const form = f.svc.get().form;
    let id: string | null = value;
    if (value !== null)
      try {
        const parsed = JSON.parse(value) as unknown;
        if (typeof parsed === 'string') id = parsed;
      } catch {
        /* уже строка id */
      }
    f.svc.setters.inputs(current => {
      const next = patchSpec(form, current, { [key]: id });
      return sameInputs(next, current) ? current : next;
    });
  }
  function restore(f: Frame, key: string, id: string) {
    if (!f.svc.get().identity) return;
    f.restoring = id;
    h.setFile(placeholder());
    if (onForm()) h.songPaint();
    void f.bridge.restore(key, id).finally(() => {
      if (f.restoring === id) f.restoring = null;
      if (S.file?.mid === RESTORE && cur() === f) {
        h.setFile(null);
        if (onForm()) h.songPaint();
      }
      if (cur() === f && h.mounted()) changed(f);
    });
  }
  /** Песня в рамке — та, что во входах: загрузка поля (мост), трек «Моих треков» или ничего */
  function songSync(f: Frame, v: CoverView) {
    const form = f.svc.get().form;
    if (!form) return;
    const key = songKey(f),
      id = songValue(form, f.svc.get().inputs),
      item = f.bridge.item(key),
      file = S.file;
    let repaint = false;
    if (v.source && !id) {
      if (!file?.mine || file.src !== v.source) {
        h.setFile(mineFile(v.source));
        repaint = true;
      }
    } else {
      if (file?.mine) {
        h.setFile(null);
        repaint = true;
      } else if (file?.mid && file.mid !== RESTORE && (!item || item.id !== file.mid)) {
        h.setFile(null);
        repaint = true;
      } else if (item && file?.mid === item.id && item.state === 'ready' && item.uploadId !== id && !f.svc.get().busy && !f.svc.derive().locked) {
        /* входы сменились не формой (форма перечитана, повтор): загрузка рамки больше не песня заказа — как родная форма, песню выбирают заново */
        f.bridge.remove(key);
        h.setFile(null);
        repaint = true;
      }
      if (!S.file && id && !f.bridge.item(key) && !f.restoring) restore(f, key, id);
    }
    if (repaint && onForm()) h.songPaint();
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  const layoutOf = (f: Frame) =>
    [server.has('own'), server.has('mine'), server.has('voice'), server.has('dur'), server.has('wish'), server.improveKind(), f.lists?.styles.map(s => s.id).join() ?? ''].join('|');
  /** Форма и входы → списки шаблона и S (без отрисовки); true — форму надо нарисовать заново */
  function syncState(f: Frame): boolean {
    const { s } = live(f);
    const form = s.form;
    if (form && (f.form !== form || filled !== f)) {
      f.form = form;
      f.lists = coverSupported(form) ? coverLists(form) : null;
      fillLists(f);
      if (!f.cloneAsked && specKey(form, 'voice_mode') && specKey(form, 'voice_id')) {
        f.cloneAsked = true;
        void readyClone().then(clone => {
          f.clone = cloneVoice(clone);
          if (cur() === f && onForm() && f.clone) h.set.sync();
        });
      }
    }
    let repaint = false;
    if (form && f.lists && f.mirror.external(s.inputs)) {
      const v = readCover(form, f.lists, s.inputs);
      repaint = apply(v);
      f.mirror.seen(s.inputs);
      songSync(f, v);
    } else if (form && f.lists) songSync(f, readCover(form, f.lists, s.inputs));
    const layout = layoutOf(f);
    if (layout !== f.layout) {
      f.layout = layout;
      repaint = true;
    }
    return repaint;
  }
  function formSync(f: Frame) {
    const repaint = syncState(f);
    if (!onForm()) return;
    if (repaint) h.paintPage();
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз в рамке */
    const now = f.svc.get();
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) h.up.say(uiText(error));
    }
    h.syncFoot();
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    formSync(f);
    f.driver.sync();
    f.bridge.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту): при каждом показе формы, если в памяти
      его нет; созданный или открытый заказ делает память устаревшей */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.syncFoot();
      });
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    return null;
  }
  /** Права на песню после перезагрузки (песня повтора, а согласия в этой вкладке ещё не было): лист в режиме подтверждения */
  function needsRights(f: Frame) {
    const { s } = live(f);
    return !s.rights && !S.file?.mine && !!s.form && !!songValue(s.form, s.inputs);
  }
  function rightsField(f: Frame): Partial<Pick<ConsentOptions, 'field' | 'fieldOn' | 'setField'>> {
    const form = f.svc.get().form;
    if (!form || !specKey(form, 'rights_confirmed')) return {};
    return {
      field: 'rights_confirmed',
      fieldOn: () => readText(specKey(f.svc.get().form, 'rights_confirmed'), f.svc.get().inputs) === 'true',
      /* как родная форма: отметка — true, снятая — ключа нет */
      setField: on =>
        f.svc.setters.inputs(current => {
          const next = patchSpec(f.svc.get().form, current, { rights_confirmed: on ? true : null });
          return sameInputs(next, current) ? current : next;
        }),
    };
  }
  function confirmRights(f: Frame, then: () => void) {
    f.consent = openConsentSheet({
      rights: () => f.svc.get().rights,
      locked: () => f.svc.derive().locked,
      setRights: on => f.svc.setters.rights(on),
      texts: RIGHTS,
      confirm: true,
      ...rightsField(f),
      onContinue: () => {
        f.consent = null;
        if (cur() === f) then();
      },
      onClose: () => {
        f.consent = null;
      },
    });
  }

  /* ---- вход заказа: тело заявки (или подсмотренной), иначе форма ---- */
  /** Заявка заказа: сервиса, а пока он не проверил владельца — подсмотренная при открытии (потом её решает сервис) */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Входы тела заявки строками, как входы формы */
  const bodyInputs = (it: Intent) =>
    Object.fromEntries(Object.entries(it.body.inputs as Record<string, unknown>).map(([key, value]) => [key, typeof value === 'string' ? value : JSON.stringify(value)]));
  /** Вид формы заказа: тело заявки, иначе S */
  function sentView(f: Frame | null): CoverView {
    const form = f?.svc.get().form;
    const it = intentOf(f);
    if (!f || !form || !f.lists || !it) return viewOf();
    return readCover(form, f.lists, bodyInputs(it));
  }
  /** Стиль заказа (копия: снимок не зависит от смены списков) */
  function styleOf(id: number | 'own'): EngineStyle {
    const style = String(id) === 'own' ? h.OWN : (h.STY.find(s => String(s.id) === String(id)) ?? h.STY[0] ?? h.OWN);
    return { ...style, c: style.c.slice() };
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      foot: chatFoot(serverFoot((opts.foot as Record<string, unknown>) ?? {}, 'cv'), 'cv'),
      T: { ...((opts.T as Record<string, unknown>) ?? {}), doneP: (r: BotRunRun) => server.doneText(r) },
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти (песня, стиль) им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        ((inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key])(...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /* ---- итог: два варианта ---- */
  const waveN = () => core().wave?.N ?? 52;
  /** Варианты готового заказа кадра */
  const variantsOf = (f: Frame) => coverVariants(f.svc.get().order);
  /** Волна варианта i на карточке итога — на месте (#cvRw0, #cvRw1: якоря не пересоздаются) */
  function paintWave(r: BotRunRun, i: number, peaks: number[]) {
    const snap = r.snap as CoverSnap;
    const next = resample(peaks, waveN());
    if (snap.peaks[i]?.join() === next.join()) return;
    snap.peaks[i] = next;
    const el = h.root()?.querySelector(`#cvRw${i}`);
    if (el) el.innerHTML = (window.BotCore as unknown as { wave: { html(p: number[]): string } }).wave.html(next);
  }
  /** Заказ готов (каждая сверка): волны и длина вариантов из итога сервера — на карточке на месте */
  function onDone(f: Frame) {
    const order = f.svc.get().order;
    const r = h.tail.run;
    if (!order || order.status !== 'done' || !r || r.st !== 'done') return;
    const vars = variantsOf(f);
    const key = `${order.id}:${order.result_revision}:${vars.map(v => `${v.index}/${v.durationMs}/${v.peaks?.length ?? 0}`).join()}`;
    if (f.doneKey === key && f.doneRun === r) return;
    f.doneKey = key;
    f.doneRun = r;
    vars.slice(0, 2).forEach((v, i) => {
      if (v.peaks) paintWave(r, i, v.peaks);
    });
    const ms = vars.find(v => v.durationMs)?.durationMs;
    const snap = r.snap as CoverSnap;
    if (ms && snap.len !== Math.max(1, Math.round(ms / 1000))) {
      snap.len = Math.max(1, Math.round(ms / 1000));
      h.rpPaint(true);
    }
  }
  /** Волна варианта из его файла (WebAudio), если сервер её не прислал: после того как файл открыли нажатием */
  function fileWave(f: Frame, r: BotRunRun, i: number, v: CoverVariant) {
    if (v.peaks) return;
    void f.media.peaks(v.index, waveN()).then(wave => {
      if (!wave || h.tail.run !== r) return;
      paintWave(r, i, wave.peaks);
      const snap = r.snap as CoverSnap;
      if (!v.durationMs && wave.durationMs > 0 && i === (h.RP.i ?? 0)) {
        snap.len = Math.max(1, Math.round(wave.durationMs / 1000));
        h.rpPaint(true);
      }
    });
  }
  /** «Назад» внутри экрана (Telegram, «назад» раздела): сначала верхний слой — «Улучшить», поле во весь экран, лист экрана или ядра */
  function back(f: Frame): boolean {
    if (cur() !== f || !h.mounted()) return false;
    const bc = window.BotCore as unknown as { improve?: { isOpen: boolean; close(): void }; editor?: { isOpen: boolean; close(): void } };
    if (bc.improve?.isOpen) {
      bc.improve.close();
      return true;
    }
    if (bc.editor?.isOpen) {
      bc.editor.close();
      return true;
    }
    if (S.sh) {
      h.sheetClose();
      return true;
    }
    const sheets = [...document.querySelectorAll<HTMLElement>('.bsheet.open[aria-hidden="false"]')];
    const top = sheets.at(-1);
    const x = top?.querySelector<HTMLElement>('[data-bsheet-x],[data-cv-shx],.xbtn');
    if (!x) return false;
    x.click();
    return true;
  }

  /* ---- привязка кадра ---- */
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) {
        frame.bridge.cancel();
        frame.consent?.close();
        frame.pick.abort();
        frame.sample.stop();
        frame.media.release();
      }
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      back: () => back(mine),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      form: null,
      lists: null,
      layout: '',
      restoring: null,
      said: '',
      consent: null,
      pick: createVoicePick(),
      sample: createVoiceSample(),
      clone: null,
      cloneAsked: false,
      mineSeq: 0,
      mineState: 'load',
      peek: null,
      doneKey: '',
      doneRun: null,
      moving: false,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string) => h.up.say(text)),
      policy: key => songPolicy(f, key),
      put: guard((item: MediaItem) => put(f, item)),
      drop: guard((item: MediaItem | null) => drop(item)),
      update: guard(() => {
        if (cur() === f && onForm()) h.syncFoot();
      }),
      consentWith: () => ({ texts: RIGHTS, ...rightsField(f) }),
      write: (key, value) => writeSong(f, key, value),
    });
    f.media = createResultMedia({ svc: b.svc, ic: name => h.ic(name) });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => h.paintPage(),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
    });
    return f;
  }

  const server: CoverServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    open() {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: песня, стиль, голос, скидка и хвост прежнего сюда не переходят */
        h.liveStop();
        h.sheetClose();
        h.setFile(null);
        Object.assign(S, { sty: 1, list: [], text: '', desc: '', open: false, vm: 'src', voice: null, dur: 'frag', from: 0, cheap: false });
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
      }
      f.formShown = false;
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      /* входы → S до первой отрисовки: подвал формы пишет S обратно во входы */
      syncState(f);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
      f.pick.abort();
      f.sample.stop();
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { price?: unknown; go?: unknown; where?: unknown };
      const offer = () => {
        const f = cur();
        return f ? studioCheapOfferAt(f.svc.get().form, basePrice(S.dur)) : null;
      };
      /* скидку «Сделать дешевле» публикует форма (Q5): без неё у подвала нет ни цены, ни действия скидки */
      return {
        /* где водяной знак («в аудио») — в листе «Не хватает Circle» словами профиля */
        get where() {
          return typeof cheap.where === 'string' ? uiText(cheap.where) : cheap.where;
        },
        get price() {
          return offer() ? cheap.price : undefined;
        },
        get go() {
          return offer() ? cheap.go : undefined;
        },
        get percent() {
          return offer()?.percent;
        },
      } as unknown as T;
    },
    art(id, o) {
      const style = id == null ? null : String(id) === 'own' ? OWN0 : (STY0.find(s => String(s.id) === String(id)) ?? STY0[0] ?? null);
      return h.artHTML(style, o ?? { res: true });
    },
    has(k) {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      if (!form) return true;
      switch (k) {
        case 'own':
          return !!f?.lists?.own;
        case 'mine':
          return published(f, 'source_order_id');
        case 'voice':
          return published(f, 'voice_mode');
        case 'dur':
          return published(f, 'duration') && !!f?.lists?.durations.length;
        default:
          return published(f, 'style_text');
      }
    },
    voice(id) {
      const want = num(id);
      if (want === undefined) return null;
      const f = cur() ?? frame;
      const own = f?.svc.get().voice;
      if (own && own.id === want) return engineVoice(own);
      if (f?.clone && f.clone.id === want) return f.clone;
      const ui = h.ui();
      for (const list of [ui.voices?.() ?? [], ui.myVoices?.() ?? []])
        for (const raw of list) {
          const v = shellVoice(raw);
          if (v && v.id === want) return v;
        }
      return null;
    },
    clone: () => cur()?.clone ?? null,
    pickVoice(id) {
      const f = cur();
      const want = num(id);
      if (!f || !h.mounted() || h.tail.run || want === undefined) return;
      if (!specKey(f.svc.get().form, 'voice_id') || f.svc.derive().locked) return;
      /* сведения голоса (voice-pick): переозвучку записи умеет только голос с is_s2s; прежний выбор прерван */
      f.pick
        .pick(want, { s2s: true })
        .then(picked => {
          if (!picked || cur() !== f) return;
          if (!picked.ok) {
            tip(null, 'Этот голос не умеет переозвучку записи');
            return;
          }
          if (f.svc.derive().locked) return;
          f.svc.setters.voice(picked.voice);
          h.set.set('voice', String(picked.voice.id));
          h.ui().buzz();
        })
        .catch(() => tip(null, 'Не удалось выбрать голос. Попробуйте ещё раз.'));
    },
    price(d, cheap) {
      const base = basePrice(d);
      if (cheap) {
        const offer = studioCheapOfferAt((cur() ?? frame)?.svc.get().form, base);
        if (offer) return offer.price;
      }
      return base;
    },
    unit() {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      if (form?.chargeable === false) return '';
      const duration = f?.lists?.durations.find(item => item.id === S.dur) ?? f?.lists?.durations[0];
      if (!duration) return '';
      return core().html.unit(
        server.price(duration.id, false),
        duration.id === 'full' ? uiText('за песню') : uiFormat('за фрагмент {0}', mmss(duration.windowMs / 1000)),
      );
    },
    note() {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      const policy = f?.lists?.policy;
      const slot = form ? songSlot(form) : null;
      const listed = record(record(record(form?.constraints).cover).song).formats;
      const formats = Array.isArray(listed) && listed.every(item => typeof item === 'string') && listed.length
        ? (listed as string[])
        : [...new Set((slot?.accept.length ? slot.accept : ['audio/mpeg', 'audio/wav', 'audio/mp4']).map(mime => NAMES[mime] ?? mime.split('/')[1]?.toUpperCase()).filter(Boolean))];
      const kinds = formats.length > 1 ? uiFormat('{0} или {1}', formats.slice(0, -1).join(', '), formats.at(-1)) : (formats[0] ?? '');
      return [kinds, uiFormat('до {0} минут', Math.round((policy?.maxMs ?? 600000) / 60000))].filter(Boolean).join(', ');
    },
    missing() {
      const f = cur();
      if (!f) return 'load';
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return '';
      if (!s.identity && !s.integrityError) return 'load';
      if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
      /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
      if (!s.form) return s.error ? '' : 'load';
      if (s.versionStale) return '';
      if (!coverSupported(s.form) || !f.lists) return 'off';
      if (!s.form.available) return 'unavailable';
      return null;
    },
    ready() {
      const f = cur();
      if (!f) return 'upload';
      const { s, d } = live(f);
      const form = s.form,
        lists = f.lists;
      if (!form || !lists) return 'off';
      const mine = !!S.file?.mine;
      if (!mine) {
        /* неподтверждённая загрузка: «Продолжить загрузку» (recovery) — кнопка активна; иначе — загрузка идёт */
        if (f.bridge.pending()) return f.bridge.stalled() && s.error === TRANSPORT && !s.busy ? '' : 'upload';
        if (!songValue(form, s.inputs)) return 'upfail';
      }
      const songMs = !mine && S.file && S.file.dur > 0 ? S.file.dur * 1000 : null;
      /* права после перезагрузки подтверждает лист при нажатии «Создать» (start): здесь кнопка остаётся активной */
      const reason = coverMissing(form, lists.policy, s.inputs, true, { songMs, own: S.sty === 'own' });
      if (reason) return reasonText(reason, lists.policy);
      if (form.chargeable !== false && server.price(S.dur, false) !== d.price) return uiText('Цена этого выбора пока не подтверждена');
      return '';
    },
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText(s?.form && !coverSupported(s.form) ? 'Создание кавера пока недоступно' : s?.error || 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем песню');
        case 'upfail':
          return uiText('Загрузите песню заново');
        default:
          return '';
      }
    },
    label() {
      const f = cur();
      if (!f) return null;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
      if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (s.form?.chargeable === false) return uiText('Создать кавер');
      return null;
    },
    improveKind() {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      if (!form || !specKey(form, 'style_text')) return '';
      const kind = record(record(form.constraints).cover).improvement_kind;
      return isImproveKind(kind) ? kind : '';
    },
    upOpts: () => ({
      accept: ACCEPT,
      /* свои строки рамки BotCore.upload («Загрузить», «Записать», «Заменить», «Убрать», отказы) — словарём профиля */
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      onPick(files) {
        const f = cur();
        const file = files[0];
        if (!f || !file) return;
        const key = songKey(f);
        /* длина — правило шаблона (песня короче 6 секунд или длиннее 10 минут в рамку не встаёт, рамка называет причину);
           тип, вес и согласие — мост */
        void mediaFile.readDuration(file, 'audio').then(ms => {
          if (cur() !== f) return;
          const { max, min } = h.limits();
          if (ms && ms > max * 1000) return h.up.say(uiFormat('Песня длиннее {0} минут', Math.round(max / 60)));
          if (ms && ms < min * 1000) return h.up.say(uiFormat('Песня короче {0} секунд', min));
          void f.bridge.pick(key, file, ms ? { durationMs: ms } : undefined);
        });
      },
    }),
    remove() {
      const f = cur();
      if (!f) return;
      f.restoring = null;
      f.bridge.remove(songKey(f));
    },
    write() {
      const f = cur();
      if (!f || !f.lists || !onForm()) return;
      const form = f.svc.get().form;
      if (!form || f.form !== form) return;
      const lists = f.lists;
      commit(f, prev => writeCover(form, lists, viewOf(), prev, { songSec: songSec() }));
    },
    canSample(id) {
      const f = cur() ?? frame;
      return !!f?.lists?.styles.find(s => String(s.id) === String(id))?.preview;
    },
    sample(id, onEnd) {
      const f = cur();
      const style = f?.lists?.styles.find(s => s.id === id);
      return !!f && !!style?.preview && f.sample.play(style.preview, onEnd);
    },
    sampleStop() {
      frame?.sample.stop();
    },
    loadMine() {
      const f = cur();
      h.MINE.splice(0);
      if (!f) return;
      const seq = ++f.mineSeq;
      f.mineState = 'load';
      void endpoints.history.list({ tab: 'audio', q: '', sort: 'new' }).then(
        page => {
          if (cur() !== f || f.mineSeq !== seq) return;
          const rows = page.items.filter(item => item.status === 'done').map(mineRow);
          h.MINE.splice(0, h.MINE.length, ...rows);
          f.mineState = rows.length ? 'ok' : 'empty';
          h.paintMine();
        },
        () => {
          if (cur() !== f || f.mineSeq !== seq) return;
          f.mineState = 'error';
          h.paintMine();
        },
      );
    },
    mineNote() {
      const f = cur();
      const state = f?.mineState ?? 'load';
      if (state === 'ok') return '';
      const text = state === 'load' ? 'Загружаем…' : state === 'empty' ? 'Пока нет готовых треков' : 'Не удалось загрузить «Мои треки».';
      return `<div class="empty pf-empty"${state === 'error' ? ' role="alert"' : ' role="status"'}><b>${h.esc(uiText(text))}</b></div>`;
    },
    chooseMine(i) {
      const f = cur();
      const row = h.MINE[i];
      if (!f || !row) return;
      if (f.svc.derive().locked) return;
      h.sheetClose();
      h.recCancel(true);
      f.restoring = null;
      /* трек «Моих треков» вместо файла: загрузка песни снимается (родная форма: clearSong) */
      f.bridge.remove(songKey(f));
      h.setFile(mineFile(row.src));
      h.songPaint();
      h.ui().buzz();
    },
    canRecord: () => !!(window.SteosUI as unknown as { canRecord?(): boolean }).canRecord?.(),
    micText: kind => uiText(kind === 'off' ? 'Запись здесь недоступна, загрузите файл' : 'Не удалось включить микрофон. Разрешите доступ или загрузите файл.'),
    recorded({ sec, blob, url }) {
      if (url)
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* адрес записи больше не нужен: мост делает свой WAV */
        }
      /* плитка записи уходит из рамки (REC.st уже idle) */
      if (onForm()) h.songPaint();
      const f = cur();
      if (!f) return;
      const { max, min } = h.limits();
      if (!blob) return h.up.say(uiText('Не удалось прочитать запись, попробуйте ещё раз'));
      if (sec < min) return h.up.say(uiFormat('Запись короче {0} секунд', min));
      void f.bridge.recorded(songKey(f), blob, max * 1000);
    },
    fplay(file) {
      if (file.url) return false;
      /* трек «Моих треков»: его звука на странице нет (Q-C3) — подсказка, а не тихий ход */
      tip(h.root()?.querySelector('#cvFc .pb') ?? null, 'Этот трек можно послушать в «Истории»');
      return true;
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (needsRights(f)) {
        confirmRights(f, () => server.start(btn));
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    steps() {
      const v = sentView(cur() ?? frame);
      return [
        ['load', 'upload', uiText('Загружаем песню'), uiText('Песня загружена')],
        ['check', 'shield', uiText('Проверяем права'), uiText('Права проверены')],
        ['split', 'wave', uiText('Отделяем голос от музыки'), uiText('Голос отделён')],
        ...(v.vm === 'voice' ? [['voice', 'mic', uiText('Меняем голос'), uiText('Голос изменён')]] : []),
        ['style', 'music', uiText('Играем в новом стиле'), uiText('Стиль готов')],
        ['mix', 'sparkle', uiText('Сводим два варианта'), uiText('Варианты готовы')],
      ];
    },
    snap() {
      const f = cur() ?? frame;
      const it = intentOf(f);
      const v = sentView(f);
      const { frag } = h.limits();
      /* песня в памяти — только если она и есть песня заказа (у трека «Моих треков» — тот же заказ-источник) */
      const form = f?.svc.get().form ?? null;
      const mineOk = !!S.file?.mine && S.file.src === v.source;
      const songOk = !!S.file && !S.file.mine && (!it || (!!form && songValue(form, bodyInputs(it)) === songValue(form, f!.svc.get().inputs)));
      const own = S.file && (mineOk || songOk) ? S.file : null;
      const song = own && own.dur > 0 ? own.dur : 0;
      const from = v.dur === 'frag' ? Math.max(0, Math.min(v.from, song ? song - frag : v.from)) : 0;
      const to = v.dur === 'frag' ? (song ? Math.min(song, from + frag) : from + frag) : song;
      const voice = v.vm === 'voice' ? v.voice : null;
      return {
        file: own ? { ...own } : { name: uiText('Песня'), size: 0, url: '', dur: 0, kind: 'audio' },
        sty: styleOf(v.sty),
        text: v.text.trim(),
        voice,
        vname: voice !== null ? (server.voice(voice)?.name ?? '') : '',
        dur: v.dur,
        from: Math.round(from),
        to: Math.round(to),
        len: Math.max(1, Math.round(to - from)),
        cheap: !!(it ? it.body.cheap : S.cheap),
        peaks: [flat(), flat()],
      };
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: { modH: 'Песня не прошла проверку', techH: 'Не получилось сделать кавер', notMade: 'кавер не создан', modWhy: 'Похоже на чужую запись: каверы делаем только на свои песни.' },
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'в аудио' });
    },
    doneText() {
      const f = cur() ?? frame;
      const order = f?.svc.get().order ?? null;
      /* родной хвост: бот уже прислал (доставка sent, delivered) или пришлёт; не присылает — текст доставки (tail-done) */
      const sent = ['sent', 'delivered'].includes(String(record(record(order).delivery).state));
      const base = esc(
        uiText(sent ? 'Мелодия ваша, звучание новое. Бот уже прислал оба варианта в чат, в отдельную тему.' : 'Мелодия ваша, звучание новое. Бот пришлёт оба варианта в чат, в отдельную тему.'),
      );
      return doneP(order, base, 'cv');
    },
    async download(i) {
      const f = cur();
      const v = f ? variantsOf(f)[i] : undefined;
      if (!f || !v) throw new Error('result_unavailable');
      return f.media.download(v.index);
    },
    picked(i) {
      /* выбор варианта — его файл (родной хвост: openResult по нажатию) и волна из файла, если сервер её не прислал */
      const f = cur();
      const r = h.tail.run;
      const v = f ? variantsOf(f)[i] : undefined;
      if (!f || !v || !r) return;
      void f.media.open(v.index).then(item => {
        if (item) fileWave(f, r, i, v);
      });
    },
    play(i, at) {
      const f = cur();
      const r = h.tail.run;
      if (!f || !r || r.st !== 'done') return true;
      const v = variantsOf(f)[i];
      const btn = h.root()?.querySelector<HTMLElement>(`[data-cv-rplay="${i}"]`) ?? null;
      if (!v) {
        tip(btn, 'Запись ещё загружается');
        return true;
      }
      const { RP } = h;
      if (RP.i !== i) {
        f.media.stop(true);
        RP.on = false;
        RP.pos = 0;
        h.rpPaint(true);
        RP.i = i;
      }
      void f.media
        .toggle(v.index, {
          kind: 'audio',
          dur: (r.snap as CoverSnap).len,
          at,
          paint: (pos, _dur, on) => {
            if (h.tail.run !== r || RP.i !== i) return;
            RP.pos = pos;
            RP.on = on;
            h.rpPaint(true);
          },
          onEnd: () => {
            if (h.tail.run !== r || RP.i !== i) return;
            RP.on = false;
            RP.pos = 0;
            h.rpPaint(true);
          },
        })
        .then(ok => {
          if (ok) fileWave(f, r, i, v);
          else if (h.tail.run === r) tip(btn, f.svc.get().error || 'Запись ещё загружается');
        });
      return true;
    },
    audioStop(rewind) {
      frame?.media.stop(rewind);
    },
    leave(go, replace) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(
        () => {
          if (replace) {
            f.bridge.cancel();
            f.restoring = null;
          }
          go();
        },
        replace,
        songKey(f),
      );
    },
    click(d, el) {
      const f = cur();
      const s = f?.svc.get();
      const r = h.tail.run;
      /* «Ещё вариант» (D10, docs/03-api.md §12.3): к форме с теми же настройками, без запроса — новый заказ создаёт «Создать» */
      if (d.cvAgain !== undefined) {
        if (f && r?.st === 'done') server.leave(() => h.tail.exit(() => h.paintPage()), false);
        return true;
      }
      if (d.cvChat !== undefined) {
        if (r?.st === 'done') chatClick({ order: s?.order, el, text: 'Тут откроется тема чата, куда бот прислал кавер', tip: (at, text) => h.ui().tip(at, text) });
        return true;
      }
      if (d.cvSettings !== undefined) {
        openSettings();
        return true;
      }
      if (d.cvTocirc !== undefined) {
        const order = s?.order;
        const pick = Number((r as (BotRunRun & { pick?: number }) | null)?.pick) || 0;
        const v = f ? variantsOf(f)[pick] : undefined;
        if (!f || !s || !order || order.status !== 'done' || r?.st !== 'done' || !v || f.moving) return true;
        f.moving = true;
        markSeen(order.id);
        void busyButton(el, () => transferCoverResult({ ownerId: s.identity?.owner, order, index: v.index, blob: f.media.file(v.index)?.blob }))
          .catch(() => {
            if (el.isConnected) tip(el, 'Не удалось передать запись. Откройте работу в «Истории» и попробуйте снова.');
          })
          .finally(() => {
            f.moving = false;
          });
        return true;
      }
      if (d.cvRetry === undefined && d.cvRchk === undefined && d.cvFix === undefined) return false;
      if (!f || f.driver.busy()) return true;
      if (d.cvRetry !== undefined) h.tail.retry();
      else if (d.cvRchk !== undefined) void f.driver.check(el);
      else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, songKey(f));
      return true;
    },
  };

  /* ---- смена языка профиля (i18n: событие steos:locale) ---- */
  /** Подписи этапов идущего заказа — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  /** Открытый экран рисуется заново на новом языке: списки шаблона (длительности, метки стилей, «Свой стиль»), хвост — TAIL.show
      (ход итога — на месте), форма — paintPage; затем обычная сверка */
  function relocale() {
    const r = h.tail.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (f.lists) fillLists(f);
    if (r) {
      h.tail.show(root);
      h.rpPaint(true);
    } else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  /* новый снимок Me (пополнение, слияние) уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.syncFoot();
  });

  instance = { bind };
  return server;
}

/** ENGINES.cover.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindCoverEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «cover» реестра движков студии: движок шаблона window.BotCover, атрибуты data-cv-*, правая панель, знак заказа «cover» */
export const COVER_ENGINE: EngineScreen = { global: 'BotCover', prefix: 'cv', side: true, kind: 'cover', bind: bindCoverEngine };

export function registerCoverEngine() {
  ENGINES.cover = COVER_ENGINE;
}
